// React
import type { FC, SVGProps } from 'react';
import { useRouter } from 'next/router';

import { useSetAtom } from 'jotai';

import useTranslation from '@/utils/hooks/translation/useTranslation';
import useAcl from '@/utils/hooks/user/useAcl';
import useUserRole from '@/utils/hooks/user/useUserRole';

// Lib
import { aclFeatures } from '@/lib/acl/aclFeatures';
import {
    CONTENT_ALBUMS,
    REPORTS_ACCOUNTS,
    SCHEDULER,
    SOCIAL_PROFILES,
    USERS_ROLES
} from '@/lib/routes/privateRoutes';

import {
    CalendarPlusOutline,
    CalendarPlusSolid,
    ReportsOutline,
    ReportsSolid,
    SocialProfileOutline,
    SocialProfileSolid,
    UserNeckOutline,
    UserNeckSolid
} from '@/components/base/Icon/svg';
import { ModalUpsell } from '@/components/composite/Modals/ModalUpsell';
import { menuOverlayAtom } from '@/components/layouts/LayoutPlatform/components/Menu/menuOverlayAtom';

type SvgComponent = FC<SVGProps<SVGSVGElement>>;

export type SingleKeyAction = {
    type: string;
    title: string;
    description?: string;
    icon?: string | SvgComponent;
    hoverIcon?: string | SvgComponent;
    acl?: {
        permissions_plan_restriction: string[];
    };
    color?: string;
    onClick?: () => void;
    is_dual?: false;
};

export type DualKeyAction = {
    type: string;
    is_dual: true;
    is_displayed: boolean;
    actions: SingleKeyAction[];
};

export type KeyActionType = SingleKeyAction | DualKeyAction;

export const useKeyActions = () => {
    const { hasAcl } = useAcl();
    const router = useRouter();
    const setMenuOverlay = useSetAtom(menuOverlayAtom);
    const { t } = useTranslation('commons');
    const { isTeamMember } = useUserRole();

    const actions: KeyActionType[] = [
        {
            type: 'create_post',
            title: 'key_actions.create_post.title',
            description: 'key_actions.create_post.description',
            icon: CalendarPlusOutline,
            hoverIcon: CalendarPlusSolid,
            color: 'icoBlue',
            onClick: () => {
                return router.push(SCHEDULER);
            }
        },
        {
            type: 'create_report',
            title: 'key_actions.create_report.title',
            description: 'key_actions.create_report.description',
            icon: ReportsOutline,
            hoverIcon: ReportsSolid,
            color: 'icoGreen',
            onClick: () => {
                return router.push(REPORTS_ACCOUNTS);
            }
        },
        {
            type: 'track_campaign',
            title: 'key_actions.track_campaign.title',
            description: 'key_actions.track_campaign.description',
            icon: 'campaign-outline',
            hoverIcon: 'campaign-solid',
            color: 'icoPurple',
            acl: {
                permissions_plan_restriction: [aclFeatures.content.campaign.read]
            },
            onClick: () => {
                if (!hasAcl(aclFeatures.content.campaign.read)) {
                    return setMenuOverlay(
                        <ModalUpsell
                            heading={t('upsell_modal.campaign.heading')}
                            description={t('upsell_modal.campaign.description')}
                            benefits={[
                                { text: t('upsell_modal.campaign.benefit1'), icon: 'check' },
                                { text: t('upsell_modal.campaign.benefit2'), icon: 'check' },
                                { text: t('upsell_modal.campaign.benefit3'), icon: 'check' },
                                { text: t('upsell_modal.campaign.benefit4'), icon: 'plus' }
                            ]}
                            illustrationPath="/images/modals/modalUpsell/campaign.png"
                            dismiss={() => setMenuOverlay('')}
                        />
                    );
                }
                return router.push(CONTENT_ALBUMS);
            }
        },
        {
            type: 'upload_media',
            is_dual: true,
            is_displayed: !isTeamMember,
            actions: [
                {
                    type: 'invite_user',
                    title: 'key_actions.invite_user',
                    icon: UserNeckOutline,
                    hoverIcon: UserNeckSolid,
                    onClick: () => {
                        return router.push(USERS_ROLES);
                    }
                },
                {
                    type: 'add_social_profile',
                    title: 'key_actions.add_social_profile',
                    icon: SocialProfileOutline,
                    hoverIcon: SocialProfileSolid,
                    onClick: () => {
                        return router.push(SOCIAL_PROFILES);
                    }
                }
            ]
        }
    ];

    return actions;
};
